// ================================================================
// PROPRIETARY & CONFIDENTIAL — Mt Forge Inc.
// Copyright (c) 2026 Mt Forge. All rights reserved.
// Unauthorized copying, reverse engineering, or AI-assisted
// reproduction of this software is strictly prohibited.
// Protected under US Copyright Law (17 U.S.C.) and international
// treaties. Patent Pending. License: PROPRIETARY
// ================================================================
import React, { useState, useMemo } from 'react';

// ── File type config ──────────────────────────────────────────────
const FILE_TYPES = {
  pdf:   { label: 'PDF',   color: '#ef4444', icon: '\u{1F4C4}' },
  doc:   { label: 'DOC',   color: '#3b82f6', icon: '\u{1F4DD}' },
  xls:   { label: 'XLS',   color: '#22c55e', icon: '\u{1F4CA}' },
  ppt:   { label: 'PPT',   color: '#f97316', icon: '\u{1F4CA}' },
  img:   { label: 'IMG',   color: '#a855f7', icon: '\u{1F5BC}' },
  zip:   { label: 'ZIP',   color: '#eab308', icon: '\u{1F4E6}' },
  other: { label: 'FILE',  color: '#64748b', icon: '\u{1F4CE}' },
};

const DEPARTMENTS = ['All', 'Executive', 'Development', 'Sales', 'Finance', 'Operations', 'HR', 'Legal'];

// ── Mock folder/file data ─────────────────────────────────────────
const INITIAL_FOLDERS = [
  {
    id: 'f1', name: 'Legal Documents', department: 'Legal', expanded: false,
    subfolders: [
      { id: 'f1-1', name: 'NDAs', department: 'Legal', expanded: false, subfolders: [], files: [
        { id: 'file-01', name: 'Standard NDA Template v3.pdf', size: '245 KB', type: 'pdf', uploadedBy: 'Jordan Lutz', date: '2026-02-08', department: 'Legal' },
        { id: 'file-02', name: 'Mutual NDA - Canstorr.pdf', size: '312 KB', type: 'pdf', uploadedBy: 'Ryan Miskulin', date: '2026-02-05', department: 'Legal' },
        { id: 'file-03', name: 'NDA Tracker Spreadsheet.xls', size: '89 KB', type: 'xls', uploadedBy: 'Crystal Lutz', date: '2026-02-03', department: 'Legal' },
      ]},
      { id: 'f1-2', name: 'IP Agreements', department: 'Legal', expanded: false, subfolders: [], files: [
        { id: 'file-04', name: 'IP Assignment Agreement.pdf', size: '198 KB', type: 'pdf', uploadedBy: 'Ryan Miskulin', date: '2026-01-28', department: 'Legal' },
        { id: 'file-05', name: 'Trade Secret Policy.pdf', size: '156 KB', type: 'pdf', uploadedBy: 'Ryan Miskulin', date: '2026-01-15', department: 'Legal' },
      ]},
      { id: 'f1-3', name: 'Employee Contracts', department: 'Legal', expanded: false, subfolders: [], files: [
        { id: 'file-06', name: 'Independent Contractor Template.doc', size: '134 KB', type: 'doc', uploadedBy: 'Crystal Lutz', date: '2026-02-01', department: 'Legal' },
        { id: 'file-07', name: 'Commission Agreement Template.doc', size: '178 KB', type: 'doc', uploadedBy: 'Ryan Miskulin', date: '2026-01-20', department: 'Legal' },
      ]},
    ],
    files: [
      { id: 'file-08', name: 'Company Formation Docs.pdf', size: '1.2 MB', type: 'pdf', uploadedBy: 'Ryan Miskulin', date: '2025-12-01', department: 'Legal' },
    ],
  },
  {
    id: 'f2', name: 'HR Documents', department: 'HR', expanded: false,
    subfolders: [
      { id: 'f2-1', name: 'Onboarding Packets', department: 'HR', expanded: false, subfolders: [], files: [
        { id: 'file-09', name: 'New Hire Welcome Kit.pdf', size: '2.1 MB', type: 'pdf', uploadedBy: 'Crystal Lutz', date: '2026-02-07', department: 'HR' },
        { id: 'file-10', name: 'Onboarding Checklist.doc', size: '67 KB', type: 'doc', uploadedBy: 'Crystal Lutz', date: '2026-02-06', department: 'HR' },
        { id: 'file-11', name: '4-Step Onboarding Guide.pdf', size: '345 KB', type: 'pdf', uploadedBy: 'Jordan Lutz', date: '2026-02-04', department: 'HR' },
      ]},
      { id: 'f2-2', name: 'Benefits Info', department: 'HR', expanded: false, subfolders: [], files: [
        { id: 'file-12', name: 'Benefits Overview 2026.pdf', size: '890 KB', type: 'pdf', uploadedBy: 'Crystal Lutz', date: '2026-01-15', department: 'HR' },
      ]},
    ],
    files: [
      { id: 'file-13', name: 'Employee Handbook v2.pdf', size: '3.4 MB', type: 'pdf', uploadedBy: 'Crystal Lutz', date: '2026-01-10', department: 'HR' },
      { id: 'file-14', name: 'Team Directory Export.xls', size: '45 KB', type: 'xls', uploadedBy: 'Crystal Lutz', date: '2026-02-09', department: 'HR' },
    ],
  },
  {
    id: 'f3', name: 'Financial Reports', department: 'Finance', expanded: false,
    subfolders: [
      { id: 'f3-1', name: 'Q1 2026 Budget', department: 'Finance', expanded: false, subfolders: [], files: [
        { id: 'file-15', name: 'Q1 Budget Draft.xls', size: '234 KB', type: 'xls', uploadedBy: 'Taylor Schenher', date: '2026-02-10', department: 'Finance' },
        { id: 'file-16', name: 'Q1 Budget Presentation.ppt', size: '4.5 MB', type: 'ppt', uploadedBy: 'Taylor Schenher', date: '2026-02-09', department: 'Finance' },
      ]},
      { id: 'f3-2', name: 'Monthly P&L', department: 'Finance', expanded: false, subfolders: [], files: [
        { id: 'file-17', name: 'January 2026 P&L.xls', size: '156 KB', type: 'xls', uploadedBy: 'Crystal Lutz', date: '2026-02-05', department: 'Finance' },
        { id: 'file-18', name: 'December 2025 P&L.xls', size: '148 KB', type: 'xls', uploadedBy: 'Crystal Lutz', date: '2026-01-08', department: 'Finance' },
      ]},
      { id: 'f3-3', name: 'Revenue Forecasts', department: 'Finance', expanded: false, subfolders: [], files: [
        { id: 'file-19', name: '2026 Revenue Forecast Model.xls', size: '567 KB', type: 'xls', uploadedBy: 'Taylor Schenher', date: '2026-02-08', department: 'Finance' },
        { id: 'file-20', name: 'SaaS Revenue Projections.pdf', size: '234 KB', type: 'pdf', uploadedBy: 'Ryan Miskulin', date: '2026-02-02', department: 'Finance' },
      ]},
    ],
    files: [
      { id: 'file-21', name: 'Annual Financial Summary 2025.pdf', size: '1.8 MB', type: 'pdf', uploadedBy: 'Taylor Schenher', date: '2026-01-20', department: 'Finance' },
    ],
  },
  {
    id: 'f4', name: 'Product', department: 'Development', expanded: false,
    subfolders: [
      { id: 'f4-1', name: 'App Specs', department: 'Development', expanded: false, subfolders: [], files: [
        { id: 'file-22', name: 'Platform Architecture Overview.pdf', size: '2.3 MB', type: 'pdf', uploadedBy: 'Jordan Lutz', date: '2026-02-10', department: 'Development' },
        { id: 'file-23', name: 'API Documentation.doc', size: '456 KB', type: 'doc', uploadedBy: 'Jordan Lutz', date: '2026-02-08', department: 'Development' },
        { id: 'file-24', name: 'Database Schema Reference.pdf', size: '189 KB', type: 'pdf', uploadedBy: 'Jordan Lutz', date: '2026-02-06', department: 'Development' },
      ]},
      { id: 'f4-2', name: 'Architecture Docs', department: 'Development', expanded: false, subfolders: [], files: [
        { id: 'file-25', name: 'Supabase Auth Flow.pdf', size: '345 KB', type: 'pdf', uploadedBy: 'Jordan Lutz', date: '2026-02-05', department: 'Development' },
        { id: 'file-26', name: 'Cloudflare Deploy Guide.doc', size: '123 KB', type: 'doc', uploadedBy: 'Jordan Lutz', date: '2026-02-03', department: 'Development' },
      ]},
      { id: 'f4-3', name: 'Release Notes', department: 'Development', expanded: false, subfolders: [], files: [
        { id: 'file-27', name: 'Release v2.0 - All Apps Rebuild.doc', size: '234 KB', type: 'doc', uploadedBy: 'Jordan Lutz', date: '2026-02-10', department: 'Development' },
        { id: 'file-28', name: 'Release v1.5 - License System.doc', size: '189 KB', type: 'doc', uploadedBy: 'Jordan Lutz', date: '2026-02-09', department: 'Development' },
      ]},
    ],
    files: [
      { id: 'file-29', name: 'Product Roadmap 2026.ppt', size: '3.2 MB', type: 'ppt', uploadedBy: 'Ryan Miskulin', date: '2026-02-01', department: 'Development' },
    ],
  },
  {
    id: 'f5', name: 'Sales', department: 'Sales', expanded: false,
    subfolders: [
      { id: 'f5-1', name: 'Pitch Decks', department: 'Sales', expanded: false, subfolders: [], files: [
        { id: 'file-30', name: 'Master Pitch Deck 2026.ppt', size: '8.9 MB', type: 'ppt', uploadedBy: 'Ryan Miskulin', date: '2026-02-10', department: 'Sales' },
        { id: 'file-31', name: 'CropPatrol Canstorr Pitch.ppt', size: '5.6 MB', type: 'ppt', uploadedBy: 'Ryan Miskulin', date: '2026-02-08', department: 'Sales' },
        { id: 'file-32', name: 'OreSync Mining Vertical Deck.ppt', size: '4.2 MB', type: 'ppt', uploadedBy: 'Lucas Mason', date: '2026-02-06', department: 'Sales' },
      ]},
      { id: 'f5-2', name: 'Client Proposals', department: 'Sales', expanded: false, subfolders: [], files: [
        { id: 'file-33', name: 'Enterprise SaaS Proposal Template.doc', size: '345 KB', type: 'doc', uploadedBy: 'Lucas Mason', date: '2026-02-07', department: 'Sales' },
        { id: 'file-34', name: 'White-Label Partnership Proposal.pdf', size: '567 KB', type: 'pdf', uploadedBy: 'Ryan Miskulin', date: '2026-02-04', department: 'Sales' },
      ]},
      { id: 'f5-3', name: 'Commission Structure', department: 'Sales', expanded: false, subfolders: [], files: [
        { id: 'file-35', name: 'Ambassador Compensation Guide.pdf', size: '234 KB', type: 'pdf', uploadedBy: 'Ryan Miskulin', date: '2026-02-01', department: 'Sales' },
      ]},
    ],
    files: [
      { id: 'file-36', name: 'Sales Playbook 2026.pdf', size: '1.4 MB', type: 'pdf', uploadedBy: 'Lucas Mason', date: '2026-02-05', department: 'Sales' },
    ],
  },
  {
    id: 'f6', name: 'Meeting Notes', department: 'Executive', expanded: false,
    subfolders: [
      { id: 'f6-1', name: 'Weekly Standups', department: 'Executive', expanded: false, subfolders: [], files: [
        { id: 'file-37', name: 'Standup Notes - Feb 10 2026.doc', size: '45 KB', type: 'doc', uploadedBy: 'Ryan Miskulin', date: '2026-02-10', department: 'Executive' },
        { id: 'file-38', name: 'Standup Notes - Feb 3 2026.doc', size: '38 KB', type: 'doc', uploadedBy: 'Ryan Miskulin', date: '2026-02-03', department: 'Executive' },
      ]},
      { id: 'f6-2', name: 'Board Meetings', department: 'Executive', expanded: false, subfolders: [], files: [
        { id: 'file-39', name: 'Board Meeting Minutes - Jan 2026.pdf', size: '234 KB', type: 'pdf', uploadedBy: 'Ryan Miskulin', date: '2026-01-30', department: 'Executive' },
      ]},
      { id: 'f6-3', name: 'Client Meetings', department: 'Executive', expanded: false, subfolders: [], files: [
        { id: 'file-40', name: 'Canstorr Meeting Summary.doc', size: '67 KB', type: 'doc', uploadedBy: 'Tyson Becker', date: '2026-02-07', department: 'Executive' },
        { id: 'file-41', name: 'IOD Kickoff Notes.doc', size: '56 KB', type: 'doc', uploadedBy: 'Jordan Lutz', date: '2026-02-02', department: 'Executive' },
      ]},
    ],
    files: [],
  },
  {
    id: 'f7', name: 'Marketing', department: 'Sales', expanded: false,
    subfolders: [
      { id: 'f7-1', name: 'Brand Assets', department: 'Sales', expanded: false, subfolders: [], files: [
        { id: 'file-42', name: 'Logo Pack - All Apps.zip', size: '12.4 MB', type: 'zip', uploadedBy: 'Shanon Lutz', date: '2026-02-09', department: 'Sales' },
        { id: 'file-43', name: 'Brand Guidelines.pdf', size: '2.8 MB', type: 'pdf', uploadedBy: 'Shanon Lutz', date: '2026-02-06', department: 'Sales' },
        { id: 'file-44', name: 'Color Palette Reference.img', size: '456 KB', type: 'img', uploadedBy: 'Shanon Lutz', date: '2026-02-04', department: 'Sales' },
      ]},
      { id: 'f7-2', name: 'Social Media', department: 'Sales', expanded: false, subfolders: [], files: [
        { id: 'file-45', name: 'Social Media Calendar Q1.xls', size: '89 KB', type: 'xls', uploadedBy: 'Shanon Lutz', date: '2026-02-08', department: 'Sales' },
        { id: 'file-46', name: 'Instagram Post Templates.zip', size: '5.6 MB', type: 'zip', uploadedBy: 'Shanon Lutz', date: '2026-02-05', department: 'Sales' },
      ]},
      { id: 'f7-3', name: 'Press Kit', department: 'Sales', expanded: false, subfolders: [], files: [
        { id: 'file-47', name: 'Press Release - Platform Launch.doc', size: '134 KB', type: 'doc', uploadedBy: 'Ryan Miskulin', date: '2026-02-10', department: 'Sales' },
        { id: 'file-48', name: 'Media Contact List.xls', size: '34 KB', type: 'xls', uploadedBy: 'Shanon Lutz', date: '2026-02-03', department: 'Sales' },
      ]},
    ],
    files: [],
  },
  {
    id: 'f8', name: 'Templates', department: 'Operations', expanded: false,
    subfolders: [],
    files: [
      { id: 'file-49', name: 'Invoice Template.xls', size: '78 KB', type: 'xls', uploadedBy: 'Crystal Lutz', date: '2026-02-06', department: 'Operations' },
      { id: 'file-50', name: 'Proposal Template.doc', size: '234 KB', type: 'doc', uploadedBy: 'Lucas Mason', date: '2026-02-04', department: 'Operations' },
      { id: 'file-51', name: 'NDA Template.pdf', size: '189 KB', type: 'pdf', uploadedBy: 'Ryan Miskulin', date: '2026-02-02', department: 'Operations' },
      { id: 'file-52', name: 'Meeting Agenda Template.doc', size: '45 KB', type: 'doc', uploadedBy: 'Crystal Lutz', date: '2026-01-28', department: 'Operations' },
      { id: 'file-53', name: 'Project Brief Template.doc', size: '67 KB', type: 'doc', uploadedBy: 'Jordan Lutz', date: '2026-01-25', department: 'Operations' },
    ],
  },
];

// ── Helpers ────────────────────────────────────────────────────────
function countAllFiles(folders) {
  let count = 0;
  for (const folder of folders) {
    count += folder.files.length;
    if (folder.subfolders) count += countAllFiles(folder.subfolders);
  }
  return count;
}

function countAllFolders(folders) {
  let count = folders.length;
  for (const folder of folders) {
    if (folder.subfolders) count += countAllFolders(folder.subfolders);
  }
  return count;
}

function getAllFiles(folders) {
  let files = [];
  for (const folder of folders) {
    files = files.concat(folder.files.map(f => ({ ...f, folderName: folder.name })));
    if (folder.subfolders) files = files.concat(getAllFiles(folder.subfolders));
  }
  return files;
}

function calcTotalSize(folders) {
  const allFiles = getAllFiles(folders);
  let totalKB = 0;
  for (const f of allFiles) {
    const match = f.size.match(/([\d.]+)\s*(KB|MB|GB)/i);
    if (match) {
      const val = parseFloat(match[1]);
      const unit = match[2].toUpperCase();
      if (unit === 'KB') totalKB += val;
      else if (unit === 'MB') totalKB += val * 1024;
      else if (unit === 'GB') totalKB += val * 1024 * 1024;
    }
  }
  if (totalKB >= 1024) return (totalKB / 1024).toFixed(1) + ' MB';
  return totalKB.toFixed(0) + ' KB';
}

function searchFolders(folders, query, dept) {
  const q = query.toLowerCase();
  let results = [];
  for (const folder of folders) {
    const folderMatch = (!q || folder.name.toLowerCase().includes(q)) && (dept === 'All' || folder.department === dept);
    const matchingFiles = folder.files.filter(f =>
      (!q || f.name.toLowerCase().includes(q) || f.uploadedBy.toLowerCase().includes(q)) &&
      (dept === 'All' || f.department === dept)
    );
    if (folderMatch || matchingFiles.length > 0) {
      results.push({ ...folder, _matchedFiles: matchingFiles });
    }
    if (folder.subfolders) {
      results = results.concat(searchFolders(folder.subfolders, query, dept));
    }
  }
  return results;
}

function getRecentUploads(folders, count) {
  const allFiles = getAllFiles(folders);
  allFiles.sort((a, b) => new Date(b.date) - new Date(a.date));
  return allFiles.slice(0, count);
}

function formatDate(dateStr) {
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function getFileTypeInfo(type) {
  return FILE_TYPES[type] || FILE_TYPES.other;
}

// ── Styles ────────────────────────────────────────────────────────
const s = {
  page: { minHeight: '100vh', backgroundColor: '#0f172a', color: '#e2e8f0', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' },
  container: { maxWidth: '1400px', margin: '0 auto' },
  headerRow: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24, flexWrap: 'wrap', gap: 16 },
  headerLeft: { },
  title: { fontSize: 24, fontWeight: 800, color: '#f1f5f9', margin: 0 },
  subtitle: { fontSize: 14, color: '#64748b', marginTop: 4 },
  headerActions: { display: 'flex', gap: 10 },
  // Stats
  statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 24 },
  statCard: { background: '#1e293b', border: '1px solid #334155', borderRadius: 12, padding: 20 },
  statLabel: { fontSize: 12, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 600 },
  statValue: { fontSize: 28, fontWeight: 800, color: '#f1f5f9', marginTop: 4 },
  // Controls
  controlBar: { display: 'flex', gap: 12, alignItems: 'center', marginBottom: 20, flexWrap: 'wrap' },
  searchInput: { flex: '1 1 280px', padding: '10px 14px', background: '#1e293b', border: '1px solid #334155', borderRadius: 8, color: '#e2e8f0', fontSize: 14, outline: 'none' },
  select: { padding: '10px 14px', background: '#1e293b', border: '1px solid #334155', borderRadius: 8, color: '#e2e8f0', fontSize: 13 },
  // Breadcrumb
  breadcrumb: { display: 'flex', alignItems: 'center', gap: 6, marginBottom: 16, fontSize: 13, flexWrap: 'wrap' },
  breadcrumbItem: { color: '#10b981', cursor: 'pointer', fontWeight: 600, background: 'none', border: 'none', fontSize: 13, padding: 0 },
  breadcrumbSep: { color: '#475569' },
  breadcrumbCurrent: { color: '#e2e8f0', fontWeight: 600 },
  // Content card
  contentCard: { background: '#1e293b', border: '1px solid #334155', borderRadius: 12, overflow: 'hidden', marginBottom: 24 },
  contentHeader: { padding: '16px 20px', borderBottom: '1px solid #334155', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  contentTitle: { fontSize: 16, fontWeight: 700, color: '#f1f5f9' },
  // Folder tree item
  folderRow: { display: 'flex', alignItems: 'center', gap: 10, padding: '12px 20px', cursor: 'pointer', borderBottom: '1px solid #0f172a', transition: 'background 0.15s' },
  folderRowHover: { background: '#172033' },
  folderIcon: { fontSize: 18, flexShrink: 0 },
  folderName: { fontSize: 14, fontWeight: 600, color: '#e2e8f0', flex: 1 },
  folderMeta: { fontSize: 12, color: '#64748b', display: 'flex', gap: 16, alignItems: 'center' },
  expandIcon: { fontSize: 10, color: '#64748b', width: 20, textAlign: 'center', flexShrink: 0, transition: 'transform 0.15s' },
  // File row
  fileRow: { display: 'flex', alignItems: 'center', gap: 12, padding: '10px 20px 10px 52px', borderBottom: '1px solid #0f172a', fontSize: 13 },
  fileIcon: { width: 32, height: 32, borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 700, flexShrink: 0 },
  fileName: { flex: 1, color: '#e2e8f0', fontWeight: 500 },
  fileMeta: { color: '#64748b', fontSize: 12, minWidth: 80, textAlign: 'right' },
  fileMetaName: { color: '#94a3b8', fontSize: 12, minWidth: 120 },
  fileMetaDate: { color: '#64748b', fontSize: 12, minWidth: 100, textAlign: 'right' },
  // Type badge
  typeBadge: { padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 700, letterSpacing: '0.5px' },
  // Buttons
  btnPrimary: { padding: '10px 18px', background: '#10b981', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 },
  btnSecondary: { padding: '10px 18px', background: '#1e293b', color: '#e2e8f0', border: '1px solid #334155', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 },
  // Modal
  overlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200 },
  modal: { background: '#1e293b', border: '1px solid #334155', borderRadius: 16, width: 520, maxWidth: '90vw', maxHeight: '85vh', overflowY: 'auto' },
  modalHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px', borderBottom: '1px solid #334155' },
  modalTitle: { fontSize: 18, fontWeight: 700, color: '#f1f5f9' },
  modalClose: { background: 'none', border: 'none', color: '#64748b', fontSize: 22, cursor: 'pointer', padding: '4px 8px', lineHeight: 1 },
  modalBody: { padding: 24 },
  modalFooter: { display: 'flex', gap: 12, justifyContent: 'flex-end', padding: '16px 24px', borderTop: '1px solid #334155' },
  formField: { display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 16 },
  formLabel: { fontSize: 13, fontWeight: 600, color: '#94a3b8' },
  formInput: { padding: '10px 14px', background: '#0f172a', border: '1px solid #334155', borderRadius: 8, color: '#e2e8f0', fontSize: 14, outline: 'none' },
  // Recent uploads section
  recentCard: { background: '#1e293b', border: '1px solid #334155', borderRadius: 12, padding: 20 },
  recentTitle: { fontSize: 16, fontWeight: 700, color: '#f1f5f9', marginBottom: 16 },
  recentItem: { display: 'flex', alignItems: 'center', gap: 12, padding: '8px 0', borderBottom: '1px solid #0f172a' },
  // Empty state
  emptyState: { padding: 40, textAlign: 'center', color: '#64748b', fontSize: 14 },
  // Search results
  searchResultFolder: { fontSize: 11, color: '#10b981', fontWeight: 600, marginBottom: 2 },
  // Dept badge
  deptBadge: { padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 600, background: 'rgba(16,185,129,0.15)', color: '#10b981', border: '1px solid rgba(16,185,129,0.3)' },
};

// ── Component ─────────────────────────────────────────────────────
export default function SharedFoldersPage() {
  const [folders, setFolders] = useState(INITIAL_FOLDERS);
  const [search, setSearch] = useState('');
  const [deptFilter, setDeptFilter] = useState('All');
  const [path, setPath] = useState([]); // breadcrumb path: array of { id, name }
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showFolderModal, setShowFolderModal] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [newFolderDept, setNewFolderDept] = useState('Operations');
  const [uploadFileName, setUploadFileName] = useState('');
  const [uploadFileType, setUploadFileType] = useState('pdf');
  const [hoveredRow, setHoveredRow] = useState(null);

  // ── Derived data ──────────────────────────────────────────────
  const totalFiles = useMemo(() => countAllFiles(folders), [folders]);
  const totalFolders = useMemo(() => countAllFolders(folders), [folders]);
  const totalSize = useMemo(() => calcTotalSize(folders), [folders]);
  const recentUploads = useMemo(() => getRecentUploads(folders, 5), [folders]);

  // ── Navigate into folders via breadcrumb path ─────────────────
  const getCurrentFolders = () => {
    let current = folders;
    for (const segment of path) {
      const found = current.find(f => f.id === segment.id);
      if (found && found.subfolders) {
        current = found.subfolders;
      } else {
        break;
      }
    }
    return current;
  };

  const getCurrentParent = () => {
    if (path.length === 0) return null;
    let current = folders;
    let parent = null;
    for (const segment of path) {
      parent = current.find(f => f.id === segment.id);
      if (parent && parent.subfolders) {
        current = parent.subfolders;
      }
    }
    return parent;
  };

  const currentFolders = getCurrentFolders();
  const currentParent = getCurrentParent();
  const currentFiles = currentParent ? currentParent.files : [];

  // ── Filter & Search ───────────────────────────────────────────
  const isSearching = search.trim().length > 0 || deptFilter !== 'All';
  const searchResults = useMemo(() => {
    if (!isSearching) return [];
    return searchFolders(folders, search, deptFilter);
  }, [folders, search, deptFilter, isSearching]);

  // ── Toggle folder expand/collapse ─────────────────────────────
  const toggleFolder = (folderId) => {
    const toggle = (list) => list.map(f => {
      if (f.id === folderId) return { ...f, expanded: !f.expanded };
      if (f.subfolders) return { ...f, subfolders: toggle(f.subfolders) };
      return f;
    });
    setFolders(toggle(folders));
  };

  // ── Navigate into folder ──────────────────────────────────────
  const navigateInto = (folder) => {
    if (folder.subfolders && folder.subfolders.length > 0) {
      setPath([...path, { id: folder.id, name: folder.name }]);
    }
  };

  const navigateTo = (index) => {
    if (index < 0) {
      setPath([]);
    } else {
      setPath(path.slice(0, index + 1));
    }
  };

  // ── Create folder ─────────────────────────────────────────────
  const handleCreateFolder = () => {
    if (!newFolderName.trim()) return;
    const newFolder = {
      id: 'f-new-' + Date.now(),
      name: newFolderName.trim(),
      department: newFolderDept,
      expanded: false,
      subfolders: [],
      files: [],
    };
    if (path.length === 0) {
      setFolders([...folders, newFolder]);
    } else {
      const addToPath = (list, pathIdx) => {
        return list.map(f => {
          if (f.id === path[pathIdx].id) {
            if (pathIdx === path.length - 1) {
              return { ...f, subfolders: [...(f.subfolders || []), newFolder] };
            }
            return { ...f, subfolders: addToPath(f.subfolders || [], pathIdx + 1) };
          }
          return f;
        });
      };
      setFolders(addToPath(folders, 0));
    }
    setNewFolderName('');
    setShowFolderModal(false);
  };

  // ── Upload simulation ─────────────────────────────────────────
  const handleUpload = () => {
    if (!uploadFileName.trim()) return;
    const sizeKB = Math.floor(Math.random() * 4000) + 50;
    const size = sizeKB >= 1024 ? (sizeKB / 1024).toFixed(1) + ' MB' : sizeKB + ' KB';
    const newFile = {
      id: 'file-new-' + Date.now(),
      name: uploadFileName.trim(),
      size,
      type: uploadFileType,
      uploadedBy: 'You',
      date: new Date().toISOString().split('T')[0],
      department: currentParent ? currentParent.department : 'Operations',
    };
    if (path.length === 0) {
      // Add to a generic root — in practice would need a target folder
      const updated = [...folders];
      if (updated.length > 0) {
        updated[0] = { ...updated[0], files: [...updated[0].files, newFile] };
      }
      setFolders(updated);
    } else {
      const addFile = (list, pathIdx) => {
        return list.map(f => {
          if (f.id === path[pathIdx].id) {
            if (pathIdx === path.length - 1) {
              return { ...f, files: [...f.files, newFile] };
            }
            return { ...f, subfolders: addFile(f.subfolders || [], pathIdx + 1) };
          }
          return f;
        });
      };
      setFolders(addFile(folders, 0));
    }
    setUploadFileName('');
    setShowUploadModal(false);
  };

  // ── Render helpers ────────────────────────────────────────────
  const renderFileTypeIcon = (type) => {
    const info = getFileTypeInfo(type);
    return (
      <div style={{ ...s.fileIcon, background: info.color + '22', color: info.color }}>
        {info.icon}
      </div>
    );
  };

  const renderTypeBadge = (type) => {
    const info = getFileTypeInfo(type);
    return (
      <span style={{ ...s.typeBadge, background: info.color + '22', color: info.color }}>
        {info.label}
      </span>
    );
  };

  const renderDeptBadge = (dept) => {
    const colors = {
      Executive: '#a855f7', Development: '#3b82f6', Sales: '#10b981',
      Finance: '#eab308', Operations: '#f97316', HR: '#ec4899', Legal: '#ef4444',
    };
    const c = colors[dept] || '#64748b';
    return (
      <span style={{ ...s.deptBadge, background: c + '22', color: c, borderColor: c + '44' }}>
        {dept}
      </span>
    );
  };

  const renderFileRow = (file, indent, folderLabel) => (
    <div
      key={file.id}
      style={{
        ...s.fileRow,
        paddingLeft: indent,
        background: hoveredRow === file.id ? '#172033' : 'transparent',
      }}
      onMouseEnter={() => setHoveredRow(file.id)}
      onMouseLeave={() => setHoveredRow(null)}
    >
      {renderFileTypeIcon(file.type)}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={s.fileName}>{file.name}</div>
        {folderLabel && <div style={s.searchResultFolder}>{folderLabel}</div>}
      </div>
      {renderTypeBadge(file.type)}
      <span style={s.fileMetaName}>{file.uploadedBy}</span>
      <span style={s.fileMeta}>{file.size}</span>
      <span style={s.fileMetaDate}>{formatDate(file.date)}</span>
    </div>
  );

  const renderFolderTree = (folderList, depth) => {
    const indent = 20 + depth * 24;
    return folderList
      .filter(f => deptFilter === 'All' || f.department === deptFilter)
      .map(folder => {
        const hasChildren = (folder.subfolders && folder.subfolders.length > 0) || folder.files.length > 0;
        const fileCount = folder.files.length + (folder.subfolders ? countAllFiles(folder.subfolders) : 0);
        const subfolderCount = folder.subfolders ? folder.subfolders.length : 0;
        return (
          <div key={folder.id}>
            <div
              style={{
                ...s.folderRow,
                paddingLeft: indent,
                background: hoveredRow === folder.id ? '#172033' : 'transparent',
              }}
              onMouseEnter={() => setHoveredRow(folder.id)}
              onMouseLeave={() => setHoveredRow(null)}
              onClick={() => toggleFolder(folder.id)}
              onDoubleClick={() => navigateInto(folder)}
            >
              <span style={{
                ...s.expandIcon,
                transform: folder.expanded ? 'rotate(90deg)' : 'rotate(0deg)',
                visibility: hasChildren ? 'visible' : 'hidden',
              }}>
                \u25B6
              </span>
              <span style={s.folderIcon}>{folder.expanded ? '\uD83D\uDCC2' : '\uD83D\uDCC1'}</span>
              <span style={s.folderName}>{folder.name}</span>
              {renderDeptBadge(folder.department)}
              <span style={{ ...s.folderMeta }}>
                <span>{subfolderCount > 0 ? subfolderCount + ' folders' : ''}</span>
                <span>{fileCount} files</span>
              </span>
            </div>
            {folder.expanded && (
              <>
                {folder.subfolders && renderFolderTree(folder.subfolders, depth + 1)}
                {folder.files.map(file => renderFileRow(file, indent + 44))}
              </>
            )}
          </div>
        );
      });
  };

  // ── Render search results ─────────────────────────────────────
  const renderSearchResults = () => {
    if (searchResults.length === 0) {
      return <div style={s.emptyState}>No files or folders match your search.</div>;
    }
    const allMatchedFiles = [];
    for (const result of searchResults) {
      const matched = result._matchedFiles || result.files;
      for (const file of matched) {
        allMatchedFiles.push({ ...file, folderName: result.name });
      }
    }
    if (allMatchedFiles.length === 0) {
      return (
        <div style={{ padding: '16px 20px' }}>
          <div style={{ fontSize: 14, color: '#94a3b8', marginBottom: 12 }}>
            {searchResults.length} folder{searchResults.length !== 1 ? 's' : ''} matched
          </div>
          {searchResults.map(r => (
            <div key={r.id} style={{ ...s.folderRow, paddingLeft: 20 }}>
              <span style={s.folderIcon}>{'\uD83D\uDCC1'}</span>
              <span style={s.folderName}>{r.name}</span>
              {renderDeptBadge(r.department)}
            </div>
          ))}
        </div>
      );
    }
    return (
      <div>
        <div style={{ padding: '12px 20px', fontSize: 13, color: '#94a3b8', borderBottom: '1px solid #334155' }}>
          {allMatchedFiles.length} file{allMatchedFiles.length !== 1 ? 's' : ''} found across {searchResults.length} folder{searchResults.length !== 1 ? 's' : ''}
        </div>
        {allMatchedFiles.map(file => renderFileRow(file, 20, file.folderName))}
      </div>
    );
  };

  // ── Main render ───────────────────────────────────────────────
  return (
    <div style={s.page}>
      <div style={s.container}>
        {/* Header */}
        <div style={s.headerRow}>
          <div style={s.headerLeft}>
            <h1 style={s.title}>Shared Folders</h1>
            <p style={s.subtitle}>Company-wide file and folder sharing system</p>
          </div>
          <div style={s.headerActions}>
            <button
              style={s.btnSecondary}
              onClick={() => setShowFolderModal(true)}
              onMouseEnter={e => { e.currentTarget.style.background = '#334155'; }}
              onMouseLeave={e => { e.currentTarget.style.background = '#1e293b'; }}
            >
              + New Folder
            </button>
            <button
              style={s.btnPrimary}
              onClick={() => setShowUploadModal(true)}
              onMouseEnter={e => { e.currentTarget.style.background = '#059669'; }}
              onMouseLeave={e => { e.currentTarget.style.background = '#10b981'; }}
            >
              \u2B06 Upload File
            </button>
          </div>
        </div>

        {/* Stats */}
        <div style={s.statsGrid}>
          <div style={s.statCard}>
            <div style={s.statLabel}>Total Files</div>
            <div style={{ ...s.statValue, color: '#10b981' }}>{totalFiles}</div>
          </div>
          <div style={s.statCard}>
            <div style={s.statLabel}>Total Folders</div>
            <div style={{ ...s.statValue, color: '#3b82f6' }}>{totalFolders}</div>
          </div>
          <div style={s.statCard}>
            <div style={s.statLabel}>Storage Used</div>
            <div style={{ ...s.statValue, color: '#a855f7' }}>{totalSize}</div>
          </div>
          <div style={s.statCard}>
            <div style={s.statLabel}>Recent Uploads</div>
            <div style={{ ...s.statValue, color: '#eab308' }}>{recentUploads.length}</div>
          </div>
        </div>

        {/* Controls */}
        <div style={s.controlBar}>
          <input
            style={s.searchInput}
            placeholder="Search files and folders..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            onFocus={e => { e.target.style.borderColor = '#10b981'; }}
            onBlur={e => { e.target.style.borderColor = '#334155'; }}
          />
          <select
            style={s.select}
            value={deptFilter}
            onChange={e => setDeptFilter(e.target.value)}
          >
            {DEPARTMENTS.map(d => (
              <option key={d} value={d}>{d === 'All' ? 'All Departments' : d}</option>
            ))}
          </select>
        </div>

        {/* Two-column layout: main + sidebar */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 24, alignItems: 'start' }}>
          {/* Main folder view */}
          <div>
            {/* Breadcrumb */}
            {!isSearching && path.length > 0 && (
              <div style={s.breadcrumb}>
                <button
                  style={s.breadcrumbItem}
                  onClick={() => navigateTo(-1)}
                >
                  Root
                </button>
                {path.map((segment, i) => (
                  <React.Fragment key={segment.id}>
                    <span style={s.breadcrumbSep}>/</span>
                    {i < path.length - 1 ? (
                      <button
                        style={s.breadcrumbItem}
                        onClick={() => navigateTo(i)}
                      >
                        {segment.name}
                      </button>
                    ) : (
                      <span style={s.breadcrumbCurrent}>{segment.name}</span>
                    )}
                  </React.Fragment>
                ))}
              </div>
            )}

            {/* Content card */}
            <div style={s.contentCard}>
              <div style={s.contentHeader}>
                <span style={s.contentTitle}>
                  {isSearching
                    ? 'Search Results'
                    : path.length > 0
                      ? path[path.length - 1].name
                      : 'All Folders'
                  }
                </span>
                {!isSearching && (
                  <span style={{ fontSize: 12, color: '#64748b' }}>
                    {currentFolders.length} folder{currentFolders.length !== 1 ? 's' : ''}
                    {currentFiles.length > 0 ? `, ${currentFiles.length} file${currentFiles.length !== 1 ? 's' : ''}` : ''}
                  </span>
                )}
              </div>

              {isSearching ? (
                renderSearchResults()
              ) : (
                <div>
                  {currentFolders.length === 0 && currentFiles.length === 0 ? (
                    <div style={s.emptyState}>This folder is empty.</div>
                  ) : (
                    <>
                      {renderFolderTree(currentFolders, 0)}
                      {currentFiles.map(file => renderFileRow(file, 20))}
                    </>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Sidebar: Recent Uploads */}
          <div style={s.recentCard}>
            <div style={s.recentTitle}>Recent Uploads</div>
            {recentUploads.map(file => (
              <div key={file.id} style={s.recentItem}>
                {renderFileTypeIcon(file.type)}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#e2e8f0', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {file.name}
                  </div>
                  <div style={{ fontSize: 11, color: '#64748b' }}>
                    {file.uploadedBy} &middot; {formatDate(file.date)}
                  </div>
                </div>
                <span style={{ fontSize: 11, color: '#64748b', flexShrink: 0 }}>{file.size}</span>
              </div>
            ))}
          </div>
        </div>

        {/* ── Upload Modal ─────────────────────────────────────── */}
        {showUploadModal && (
          <div style={s.overlay} onClick={() => setShowUploadModal(false)}>
            <div style={s.modal} onClick={e => e.stopPropagation()}>
              <div style={s.modalHeader}>
                <span style={s.modalTitle}>Upload File</span>
                <button style={s.modalClose} onClick={() => setShowUploadModal(false)}>&times;</button>
              </div>
              <div style={s.modalBody}>
                <div style={s.formField}>
                  <label style={s.formLabel}>File Name</label>
                  <input
                    style={s.formInput}
                    placeholder="e.g. Q1 Report.pdf"
                    value={uploadFileName}
                    onChange={e => setUploadFileName(e.target.value)}
                    onFocus={e => { e.target.style.borderColor = '#10b981'; }}
                    onBlur={e => { e.target.style.borderColor = '#334155'; }}
                  />
                </div>
                <div style={s.formField}>
                  <label style={s.formLabel}>File Type</label>
                  <select
                    style={{ ...s.formInput, cursor: 'pointer' }}
                    value={uploadFileType}
                    onChange={e => setUploadFileType(e.target.value)}
                  >
                    {Object.entries(FILE_TYPES).map(([key, val]) => (
                      <option key={key} value={key}>{val.label} ({val.icon})</option>
                    ))}
                  </select>
                </div>
                <div style={s.formField}>
                  <label style={s.formLabel}>Destination</label>
                  <input
                    style={{ ...s.formInput, background: '#0f172a', color: '#64748b' }}
                    value={path.length > 0 ? path.map(p => p.name).join(' / ') : 'Root (first folder)'}
                    readOnly
                  />
                </div>
                <div style={{ padding: '16px', background: '#0f172a', borderRadius: 8, border: '1px dashed #334155', textAlign: 'center', color: '#64748b', fontSize: 13, marginBottom: 16 }}>
                  <div style={{ fontSize: 32, marginBottom: 8 }}>{'\uD83D\uDCC4'}</div>
                  <div>Drag and drop files here, or click to browse</div>
                  <div style={{ fontSize: 11, marginTop: 4, color: '#475569' }}>Simulated upload - no actual file transfer</div>
                </div>
              </div>
              <div style={s.modalFooter}>
                <button
                  style={s.btnSecondary}
                  onClick={() => setShowUploadModal(false)}
                >
                  Cancel
                </button>
                <button
                  style={{ ...s.btnPrimary, opacity: uploadFileName.trim() ? 1 : 0.5, cursor: uploadFileName.trim() ? 'pointer' : 'not-allowed' }}
                  onClick={handleUpload}
                  disabled={!uploadFileName.trim()}
                >
                  Upload
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Create Folder Modal ──────────────────────────────── */}
        {showFolderModal && (
          <div style={s.overlay} onClick={() => setShowFolderModal(false)}>
            <div style={s.modal} onClick={e => e.stopPropagation()}>
              <div style={s.modalHeader}>
                <span style={s.modalTitle}>Create New Folder</span>
                <button style={s.modalClose} onClick={() => setShowFolderModal(false)}>&times;</button>
              </div>
              <div style={s.modalBody}>
                <div style={s.formField}>
                  <label style={s.formLabel}>Folder Name</label>
                  <input
                    style={s.formInput}
                    placeholder="e.g. Q2 Planning"
                    value={newFolderName}
                    onChange={e => setNewFolderName(e.target.value)}
                    onFocus={e => { e.target.style.borderColor = '#10b981'; }}
                    onBlur={e => { e.target.style.borderColor = '#334155'; }}
                    onKeyDown={e => { if (e.key === 'Enter') handleCreateFolder(); }}
                  />
                </div>
                <div style={s.formField}>
                  <label style={s.formLabel}>Department</label>
                  <select
                    style={{ ...s.formInput, cursor: 'pointer' }}
                    value={newFolderDept}
                    onChange={e => setNewFolderDept(e.target.value)}
                  >
                    {DEPARTMENTS.filter(d => d !== 'All').map(d => (
                      <option key={d} value={d}>{d}</option>
                    ))}
                  </select>
                </div>
                <div style={s.formField}>
                  <label style={s.formLabel}>Location</label>
                  <input
                    style={{ ...s.formInput, background: '#0f172a', color: '#64748b' }}
                    value={path.length > 0 ? path.map(p => p.name).join(' / ') : 'Root'}
                    readOnly
                  />
                </div>
              </div>
              <div style={s.modalFooter}>
                <button
                  style={s.btnSecondary}
                  onClick={() => setShowFolderModal(false)}
                >
                  Cancel
                </button>
                <button
                  style={{ ...s.btnPrimary, opacity: newFolderName.trim() ? 1 : 0.5, cursor: newFolderName.trim() ? 'pointer' : 'not-allowed' }}
                  onClick={handleCreateFolder}
                  disabled={!newFolderName.trim()}
                >
                  Create Folder
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
